import { api } from './client';

export interface PlayAccount {
  id: number;
  name: string;
  sa_client_email: string;
  enabled: number;
  last_error: string | null;
  created_at: number;
  gcs_bucket_id: string | null;
  developer_name: string | null;
}

export interface PlayApp {
  id: number;
  account_id: number;
  package_name: string;
  title: string | null;
  app_id: string | null;
  release_notes_keywords: string | null;
  last_error: string | null;
  created_at: number;
}

export interface PlayListing {
  language: string;
  title: string;
  shortDescription: string;
  fullDescription: string;
  video: string;
}

const BASE = '/admin/play';

export const listAccounts = () => api.get<PlayAccount[]>(`${BASE}/accounts`);
export const createAccount = (input: { name: string; sa_json?: string }) =>
  api.post<{ id: number }>(`${BASE}/accounts`, input);
export const updateAccount = (id: number, input: { name?: string; enabled?: boolean; sa_json?: string; gcs_bucket_id?: string | null; developer_name?: string | null }) =>
  api.patch<{ id: number }>(`${BASE}/accounts/${id}`, input);
export const deleteAccount = (id: number) => api.delete<{ id: number }>(`${BASE}/accounts/${id}`);

export const listApps = (accountId: number) => api.get<PlayApp[]>(`${BASE}/accounts/${accountId}/apps`);
export const registerApp = (accountId: number, input: { package_name: string; app_id: string }) =>
  api.post<{ id: number }>(`${BASE}/accounts/${accountId}/apps`, input);
export const deleteApp = (appId: number) => api.delete<{ id: number }>(`${BASE}/apps/${appId}`);

export const getListings = (appId: number) => api.get<PlayListing[]>(`${BASE}/apps/${appId}/listings`);
export const saveListing = (
  appId: number, lang: string,
  input: { title: string; shortDescription: string; fullDescription: string; video?: string },
) => api.put<PlayListing>(`${BASE}/apps/${appId}/listings/${encodeURIComponent(lang)}`, input);
export const deleteListing = (appId: number, lang: string) =>
  api.delete<{ language: string }>(`${BASE}/apps/${appId}/listings/${encodeURIComponent(lang)}`);

export type ImageType = 'icon' | 'featureGraphic' | 'phoneScreenshots';

export interface PlayImage {
  id: string;
  url: string;
  sha256: string;
}

export const listImages = (appId: number, lang: string, imageType: ImageType) =>
  api.get<PlayImage[]>(`${BASE}/apps/${appId}/images/${encodeURIComponent(lang)}/${imageType}`);

// Whole live state (text + all image types for every locale) read in a single
// Play edit. The editor reads its live layer from here instead of one request
// per (locale × image type), which is what kept blowing the edit-creation quota.
export interface PlaySnapshot {
  listings: PlayListing[];
  images: Record<string, Partial<Record<ImageType, PlayImage[]>>>;
}

export const getSnapshot = (appId: number) =>
  api.get<PlaySnapshot>(`${BASE}/apps/${appId}/snapshot`);

// Raw PUT of the image bytes; returns the updated image list for that type.
export const uploadImage = (appId: number, lang: string, imageType: ImageType, file: File) =>
  api.upload<PlayImage[]>(`${BASE}/apps/${appId}/images/${encodeURIComponent(lang)}/${imageType}`, file, file.type);

export const deleteImage = (appId: number, lang: string, imageType: ImageType, imageId: string) =>
  api.delete<PlayImage[]>(`${BASE}/apps/${appId}/images/${encodeURIComponent(lang)}/${imageType}/${encodeURIComponent(imageId)}`);

export const deleteAllImages = (appId: number, lang: string, imageType: ImageType) =>
  api.delete<{ imageType: ImageType; lang: string }>(`${BASE}/apps/${appId}/images/${encodeURIComponent(lang)}/${imageType}`);

// ---------- Drafts ----------
// Drafts are stored server-side (D1 + R2) and are NOT sent to Google until
// submitDrafts() commits the selected locales for review.

export type DraftImageItem =
  | { kind: 'live'; id: string }
  | { kind: 'media'; key: string; contentType: string; url?: string };

export interface ListingDraft {
  language: string;
  title: string;
  shortDescription: string;
  fullDescription: string;
  video: string;
  updated_at: number;
}

export interface ImageDraft {
  language: string;
  imageType: ImageType;
  items: DraftImageItem[];
  updated_at: number;
}

export interface AppDrafts {
  listings: ListingDraft[];
  images: ImageDraft[];
}

export const getDrafts = (appId: number) => api.get<AppDrafts>(`${BASE}/apps/${appId}/drafts`);

export interface TranslatedListing {
  title: string;
  shortDescription: string;
  fullDescription: string;
}

export const translateListing = (input: {
  sourceLang: string; targetLang: string;
  title: string; shortDescription: string; fullDescription: string;
}) => api.post<TranslatedListing>(`${BASE}/translate-listing`, input);

export interface GeneratedListing {
  title: string;
  shortDescription: string;
  fullDescription: string;
  fits: boolean;
}

export const generateListing = (input: {
  sourceLang: string; targetLang: string;
  title: string; shortDescription: string; fullDescription: string;
}) => api.post<GeneratedListing>(`${BASE}/generate-listing`, input);

export const getTargetLocales = (appId: number) =>
  api.get<{ locales: string[] }>(`${BASE}/apps/${appId}/target-locales`);

export const setTargetLocales = (appId: number, locales: string[]) =>
  api.put<{ locales: string[] }>(`${BASE}/apps/${appId}/target-locales`, { locales });

export const ocrCaption = (appId: number, input: { imageKey: string; imageUrl: string; sourceLang: string }) =>
  api.post<{ caption: string }>(`${BASE}/apps/${appId}/screenshot-ocr`, input);

export const saveCaption = (appId: number, input: { imageKey: string; sourceLang: string; caption: string }) =>
  api.put<{ imageKey: string }>(`${BASE}/apps/${appId}/screenshot-caption`, input);

export const translateCaption = (input: { sourceLang: string; targetLang: string; text: string }) =>
  api.post<{ text: string }>(`${BASE}/translate-caption`, input);

export const renderScreenshot = (appId: number, input: { imageUrl: string; imageKey: string; caption: string; targetLang: string }) =>
  api.post<{ key: string; url: string; contentType: string }>(`${BASE}/apps/${appId}/render-screenshot`, input);

// ---- AI screenshot generation (gpt-image-2) ----
// One brief -> a plan of N distinct scenes sharing one style guide -> one image
// per call, so progress is visible and a single frame can be redone.

export interface ScreenshotScene { caption: string; scene: string }
/** `subject` is what the app is, in one line — send it back with every frame. */
export interface ScreenshotPlan { style: string; subject: string; scenes: ScreenshotScene[] }

/**
 * Where a set's look comes from: the brand pack of the project the app was made
 * from (or what its wizard run froze), the stub it ships on and the stub's real
 * UI frames. Same shape from the Play panel and the Share tab.
 */
export interface ScreenshotContext {
  source: 'project' | 'wizard' | 'none';
  project_name: string | null;
  brand: string;
  shot_orient: 'portrait' | 'landscape';
  phone_mockups: boolean;
  has_keywords: boolean;
  /** The base stub's name, when known. */
  stub: string | null;
  /** Real UI frames in the stub's library. */
  ref_count: number;
  ui_refs_default: boolean;
}

/** The two switches a set can be planned with; the server looks the values up itself. */
export interface ScreenshotContextSwitches { use_brand: boolean; ui_refs: boolean }

export const getScreenshotContext = (appId: number) =>
  api.get<ScreenshotContext>(`${BASE}/apps/${appId}/screenshot-context`);

export const planScreenshots = (appId: number, input: {
  lang: string; count: number; brief: string;
  title?: string; shortDescription?: string; fullDescription?: string;
} & Partial<ScreenshotContextSwitches>) => api.post<ScreenshotPlan>(`${BASE}/apps/${appId}/screenshot-plan`, input);

export const generateScreenshot = (appId: number, input: {
  lang: string; style: string; scene: string; caption: string; index: number; total: number;
  subject: string;
} & Partial<ScreenshotContextSwitches>) =>
  api.post<{ key: string; url: string; contentType: string }>(`${BASE}/apps/${appId}/generate-screenshot`, input);

/**
 * The feature graphic as drawn: a 1024×500 PNG plus the tagline, composition
 * and style it was drawn from, so a redo can keep or edit them.
 */
export interface FeatureGraphicResult {
  key: string; url: string; contentType: string;
  tagline: string; scene: string; style: string;
}

/** What a banner draw takes: the set's style/subject when it joins a set; no scene = let the AI plan it. */
export interface FeatureGraphicInput {
  style?: string; subject?: string; brief?: string;
  /** The operator's tagline; absent = the planner writes one, '' = the app name alone. */
  tagline?: string;
  scene?: string;
}

/** Draws the locale's feature graphic into draft media (the caller saves it into the draft). */
export const generateFeatureGraphic = (appId: number, input: FeatureGraphicInput & {
  lang: string; title: string; shortDescription?: string; fullDescription?: string;
} & Partial<ScreenshotContextSwitches>) =>
  api.post<FeatureGraphicResult>(`${BASE}/apps/${appId}/generate-feature-graphic`, input);

export type CopyMode = 'replace' | 'append';

export interface CopyScreenshotsResult {
  source: string;
  frames: number;
  targets: { lang: string; added: number; skipped: number }[];
}

/**
 * Copies one locale's phone screenshots (its draft, else live) into other
 * locales' drafts on the server — no re-upload. `targetLangs` null = every locale.
 */
export const copyScreenshots = (appId: number, input: { sourceLang: string; targetLangs: string[] | null; mode: CopyMode }) =>
  api.post<CopyScreenshotsResult>(`${BASE}/apps/${appId}/drafts/images/copy`, {
    sourceLang: input.sourceLang, mode: input.mode,
    ...(input.targetLangs ? { targetLangs: input.targetLangs } : { all: true }),
  });

// ---- Bulk localization (background job) ----

export interface LocJobCaptionInput {
  imageKey: string;
  imageUrl: string;
  caption: string;
  position: number;
}

export interface LocJobInput {
  sourceLang: string;
  langs: string[];
  doText: boolean;
  doScreenshots: boolean;
  title: string;
  shortDescription: string;
  fullDescription: string;
  /** Per-target-locale existing video, so regenerating a draft can't drop it. */
  videos: Record<string, string>;
  captions: LocJobCaptionInput[];
}

export interface LocJobStatus {
  job: {
    id: string;
    sourceLang: string;
    status: 'pending' | 'running' | 'done' | 'failed' | 'cancelled';
    doText: boolean;
    doScreenshots: boolean;
    createdAt: number;
    finishedAt: number | null;
  } | null;
  counts?: { total: number; done: number; failed: number; pending: number; running: number };
  errors?: { lang: string; kind: string; position: number; error: string }[];
  overLimitLangs?: string[];
}

export const createLocJob = (appId: number, input: LocJobInput) =>
  api.post<{ jobId: string; taskCount: number }>(`${BASE}/apps/${appId}/loc-job`, input);

export const getLocJob = (appId: number) =>
  api.get<LocJobStatus>(`${BASE}/apps/${appId}/loc-job`);

export const cancelLocJob = (appId: number, jobId: string) =>
  api.post<{ cancelled: boolean }>(`${BASE}/apps/${appId}/loc-job/${jobId}/cancel`, {});

export const saveListingDraft = (
  appId: number, lang: string,
  input: { title: string; shortDescription: string; fullDescription: string; video?: string },
) => api.put<{ language: string }>(`${BASE}/apps/${appId}/drafts/listings/${encodeURIComponent(lang)}`, input);

export const deleteListingDraft = (appId: number, lang: string) =>
  api.delete<{ language: string }>(`${BASE}/apps/${appId}/drafts/listings/${encodeURIComponent(lang)}`);

export const saveImageDraft = (appId: number, lang: string, imageType: ImageType, items: DraftImageItem[]) =>
  api.put<{ language: string }>(`${BASE}/apps/${appId}/drafts/images/${encodeURIComponent(lang)}/${imageType}`, { items });

export const deleteImageDraft = (appId: number, lang: string, imageType: ImageType) =>
  api.delete<{ language: string }>(`${BASE}/apps/${appId}/drafts/images/${encodeURIComponent(lang)}/${imageType}`);

// Raw PUT of the image bytes; stored in R2 under play-drafts/, previewable at the returned url.
export const uploadDraftMedia = (appId: number, file: File) =>
  api.upload<{ key: string; url: string }>(`${BASE}/apps/${appId}/drafts/media`, file, file.type);

export const submitDrafts = (appId: number, locales: string[]) =>
  api.post<{ submittedLocales: string[] }>(`${BASE}/apps/${appId}/drafts/submit`, { locales });

// ---------- Apps Setup listing package ----------
// Localizations generated by the AI App Wizard and saved with the app at
// creation time. Importing copies them into the drafts (review) layer only —
// nothing is sent to Google.

export interface WizardPackageInfo {
  available: boolean;
  langs: string[];
  screenshots: number;
}

export const getWizardPackage = (appId: number) =>
  api.get<WizardPackageInfo>(`${BASE}/apps/${appId}/wizard-package`);

export const importWizardPackage = (appId: number, langs?: string[]) =>
  api.post<{ listings: number; images: number }>(
    `${BASE}/apps/${appId}/wizard-package/import`,
    langs ? { langs } : {},
  );

// ---------- Submission history ----------
// Drafts are archived (not lost) when submitted; these power viewing and
// restoring a previously-submitted draft back into the editable draft tables.

export interface ListingPayload {
  title: string; shortDescription: string; fullDescription: string; video: string;
}

export interface DraftHistoryEntry {
  id: number;
  language: string;
  kind: 'listing' | 'image';
  imageType: ImageType | null;
  payload: ListingPayload | DraftImageItem[];
  submitted_at: number;
}

export const getDraftHistory = (appId: number) =>
  api.get<DraftHistoryEntry[]>(`${BASE}/apps/${appId}/drafts/history`);

export const restoreDraftHistory = (appId: number, ids: number[]) =>
  api.post<{ restored: number }>(`${BASE}/apps/${appId}/drafts/history/restore`, { ids });

export type ReleaseStatus = 'draft' | 'inProgress' | 'halted' | 'completed';

export interface TrackRelease {
  name?: string;
  versionCodes?: string[];
  status: ReleaseStatus;
  userFraction?: number;
  releaseNotes?: { language: string; text: string }[];
}

export interface Track {
  track: string;
  releases: TrackRelease[];
}

export type Rollout = { type: 'staged'; fraction: number } | { type: 'full' };

export const listTracks = (appId: number) => api.get<Track[]>(`${BASE}/apps/${appId}/tracks`);
export const setRollout = (appId: number, track: string, fraction: number) =>
  api.post<Track>(`${BASE}/apps/${appId}/tracks/${encodeURIComponent(track)}/rollout`, { fraction });
export const haltTrack = (appId: number, track: string) =>
  api.post<Track>(`${BASE}/apps/${appId}/tracks/${encodeURIComponent(track)}/halt`);
export const resumeTrack = (appId: number, track: string) =>
  api.post<Track>(`${BASE}/apps/${appId}/tracks/${encodeURIComponent(track)}/resume`);
export const completeTrack = (appId: number, track: string) =>
  api.post<Track>(`${BASE}/apps/${appId}/tracks/${encodeURIComponent(track)}/complete`);
export const promoteTrack = (appId: number, fromTrack: string, toTrack: string, rollout: Rollout) =>
  api.post<Track>(`${BASE}/apps/${appId}/tracks/${encodeURIComponent(fromTrack)}/promote`, { toTrack, rollout });

export type BuildKind = 'aab' | 'apk';

export interface UploadedBuild {
  versionCode: number;
  track: string;
  rollout: string;
}

// Raw PUT of the build bytes; rollout is "draft" | "full" | a fraction string.
export const uploadBuild = (appId: number, track: string, kind: BuildKind, rolloutParam: string, file: File) =>
  api.upload<UploadedBuild>(
    `${BASE}/apps/${appId}/builds/${encodeURIComponent(track)}?kind=${kind}&rollout=${encodeURIComponent(rolloutParam)}`,
    file,
    'application/octet-stream',
  );

// ---------- Release notes ("What's new") ----------

/** Play's hard per-locale cap; the worker rejects anything longer. */
export const RELEASE_NOTES_MAX = 500;

export interface ReleaseNoteDraft { language: string; text: string }
/** `fits: false` — the model kept overflowing and the text was trimmed to fit. */
export interface GeneratedNote { text: string; fits: boolean }

export const composeReleaseNote = (input: { lang: string; brief: string; appName?: string; keywords?: string[] }) =>
  api.post<GeneratedNote>(`${BASE}/release-notes/compose`, input);

export const translateReleaseNote = (input: { sourceLang: string; targetLang: string; text: string }) =>
  api.post<GeneratedNote>(`${BASE}/release-notes/translate`, input);

// Attached after an upload or after the operator completes an existing release.
export const setReleaseNotes = (
  appId: number, track: string, versionCode: number | string | null, notes: ReleaseNoteDraft[],
) => api.put<Track>(
  `${BASE}/apps/${appId}/tracks/${encodeURIComponent(track)}/release-notes`,
  { versionCode, notes },
);

// ---------- Keyword acquisition stats ----------

export interface KeywordRow {
  keyword: string;
  visitors: number;
  acquisitions: number;
  conversion_rate: number;
}
export interface KeywordSyncStatus {
  month: string;
  last_updated: string | null;
  last_run_at: number | null;
  status: string | null;
  error: string | null;
}

export interface KeywordCountry { country: string; visitors: number; acquisitions: number }
export interface KeywordCountries {
  countries: KeywordCountry[];
  synced_at: number | null;
  error: string | null;
  can_pull: boolean;
  queued: boolean;
}

const rangeQs = (from?: string, to?: string, country?: string) => {
  const qs = new URLSearchParams();
  if (from) qs.set('from', from);
  if (to) qs.set('to', to);
  if (country) qs.set('country', country);
  return qs.toString() ? `?${qs.toString()}` : '';
};

export const getKeywords = (appId: number, from?: string, to?: string, country?: string) =>
  api.get<KeywordRow[]>(`${BASE}/apps/${appId}/keywords${rangeQs(from, to, country)}`);
export const getKeywordCountries = (appId: number, from?: string, to?: string) =>
  api.get<KeywordCountries>(`${BASE}/apps/${appId}/keywords/countries${rangeQs(from, to)}`);
export const pullKeywordGeo = (appId: number) =>
  api.post<{ queued: boolean }>(`${BASE}/apps/${appId}/keywords/geo-sync`, {});
export const getKeywordSyncStatus = (appId: number) =>
  api.get<KeywordSyncStatus[]>(`${BASE}/apps/${appId}/keywords/sync-status`);
export const syncKeywords = (appId: number) =>
  api.post<{ months: number; rows: number }>(`${BASE}/apps/${appId}/keywords/sync`, {});

// ---------- Android vitals (crash / ANR rate) ----------

export interface PlayVitalsRow {
  app_id: number;
  package_name: string;
  title: string | null;
  account_id: number;
  account_name: string;
  date: string | null;          // yyyy-mm-dd, America/Los_Angeles
  crash_rate: number | null;    // 0..1
  anr_rate: number | null;      // 0..1
  distinct_users: number | null;
  status: 'ok' | 'no_data' | 'error' | null;
  error: string | null;
  last_run_at: number | null;
}

/** Read-only state of the Reporting API switch in a service account's own GCP project. */
export interface ApiProbeRow {
  account_id: number;
  account_name: string;
  project_id: string | null;
  api_state: string | null;      // 'ENABLED' | 'DISABLED' | null when unreadable
  can_enable: boolean;           // service account may flip the switch itself
  error: string | null;
}

export const probeReportingApi = (accountId?: number) =>
  api.post<ApiProbeRow[]>(`${BASE}/vitals/api-probe`, accountId ? { accountId } : {});

export const listVitals = () => api.get<PlayVitalsRow[]>(`${BASE}/vitals`);
export const syncVitals = () =>
  api.post<{ synced: number; noData: number; failed: number }>(`${BASE}/vitals/sync`, {});

// ---------- Per-geo store rating (external Play scraper) ----------

export interface GeoRating {
  country: string;          // lowercase ISO2
  score: number | null;     // null = never fetched, or every fetch so far failed
  ratings: number | null;   // global review count, not rendered
  available: number | null; // 0/1, app listed in that country
  error: string | null;
  fetched_at: number | null;
}

export const getGeoRatings = (appId: number) =>
  api.get<GeoRating[]>(`${BASE}/apps/${appId}/geo-ratings`);
export const setGeoRatingCountries = (appId: number, countries: string[]) =>
  api.put<GeoRating[]>(`${BASE}/apps/${appId}/geo-ratings/countries`, { countries });
export const refreshGeoRatings = (appId: number) =>
  api.post<GeoRating[]>(`${BASE}/apps/${appId}/geo-ratings/refresh`, {});
